/**
 * Invites API — TanStack Query mutation factory.
 *
 * Phase-2 Wave-3 W3.T3. Wraps the `POST /invites` create endpoint
 * (`CreateInviteRequest` from @aris/shared, including the required
 * `fullName` field per V2-FIX-01 / G1).
 *
 * On success we invalidate `qk.users.all` so the Pending tab on the
 * Employees page picks up the new row without a manual refetch.
 *
 * The server's response carries `inviteUrl` (relative path) + `expiresAt`
 * (ISO string). The dev-only `devToken` field that the BE returns under
 * non-prod NODE_ENV is intentionally NOT typed here — the FE never
 * consumes it; the modal reads `inviteUrl`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CreateInviteRequest } from '@aris/shared';

import { apiFetch } from '@/lib/fetcher';
import { qk } from '@/lib/query-keys';

export interface CreateInviteResult {
  inviteUrl: string;
  expiresAt: string;
}

export function useCreateInviteMutation() {
  const qc = useQueryClient();
  return useMutation<CreateInviteResult, unknown, CreateInviteRequest>({
    mutationFn: (body) => apiFetch<CreateInviteResult>('/invites', { method: 'POST', body }),
    onSuccess: () => {
      // New PENDING user appears in the list — bust every users-* key.
      qc.invalidateQueries({ queryKey: qk.users.all, refetchType: 'active' });
    },
  });
}
